import React, { useState, useEffect } from "react";
import { Button, RadioInput } from "components";
import { HRAgencyObjectivesData } from "../types";
import { Controller, useForm } from "react-hook-form";

interface HRAgencyObjectivesProps {
  submit: (data: HRAgencyObjectivesData) => void;
  init: HRAgencyObjectivesData;
  goBack: () => void;
}

const industryFocusOptions = [
  { label: "Single Industry", value: "single_industry" },
  { label: "Multiple Industries", value: "multiple_industries" },
];

const clientIndustryOptions = [
  { label: "Tech", value: "tech" },
  { label: "FMCG", value: "fmcg" },
  { label: "Creative", value: "creative" },
  { label: "Others", value: "others" },
];

const clientValueOptions = [
  { label: "Work Independently", value: "work_independently" },
  { label: "Collaborate Effectively", value: "collaborate_effectively" },
  { label: "Pursue Excellence", value: "pursue_excellence" },
  { label: "Act as Brand Ambassadors", value: "brand_ambassadors" },
  { label: "Demonstrate Passion", value: "demonstrate_passion" },
  { label: "Exhibit Charisma in Leadership", value: "exhibit_charisma" },
  { label: "Drive Financial Success", value: "drive_financial_success" },
];

const agencyFocusOptions = [
  { label: "Operational Roles", value: "operational_roles" },
  { label: "Technical Roles", value: "technical_roles" },
  { label: "Creative Roles", value: "creative_roles" },
  { label: "Business Roles", value: "business_roles" },
  { label: "Leadership Roles", value: "leadership_roles" },
  { label: "Others", value: "others" },
];

export const HRAgencyObjectivesForm: React.FC<HRAgencyObjectivesProps> = ({
  submit,
  init,
  goBack
}) => {
  const [formData, setFormData] = useState<HRAgencyObjectivesData>(
    {
      usage: init?.usage || [],
      culture: init?.culture || [],
      roleTypes: init?.roleTypes || [],
      hrManagement: init?.hrManagement || "",
      clientIndustries: init?.clientIndustries || "",
      clientValues: init?.clientValues || [],
      agencyFocus: init?.agencyFocus || [],
      assessmentRoles: init?.assessmentRoles || [],
      clientIndustriesList: init?.clientIndustriesList || [],
    }
  );
  const [showValidationError, setShowValidationError] = useState(false);

  const {
    register,
    formState: { errors },
    control,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      clientIndustries: init?.clientIndustries || "",
    }
  });

  // Sync form state when init changes (e.g., when navigating back from step 3)
  useEffect(() => {
    if (init) {
      setFormData({
        usage: init.usage || [],
        culture: init.culture || [],
        roleTypes: init.roleTypes || [],
        hrManagement: init.hrManagement || "",
        clientIndustries: init.clientIndustries || "",
        clientValues: init.clientValues || [],
        agencyFocus: init.agencyFocus || [],
        assessmentRoles: init.assessmentRoles || [],
        clientIndustriesList: init.clientIndustriesList || [],
      });
      setValue("clientIndustries", init.clientIndustries || "");
    }
  }, [init, setValue]);

  const watchedClientIndustries = watch("clientIndustries");

  const handleMultiSelect = (category: keyof HRAgencyObjectivesData, value: string) => {
    if (category === "clientIndustries" || category === "hrManagement") {
      setFormData((prev) => ({ ...prev, [category]: value }));
      if (category === "clientIndustries") {
        setValue("clientIndustries", value);
        // Reset clientIndustriesList when switching to single industry
        if (value === "single_industry") {
          setFormData((prev) => ({ ...prev, clientIndustriesList: [] }));
        }
      }
      return;
    }

    const currentValues = Array.isArray(formData[category]) ? (formData[category] as string[]) : [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter((item) => item !== value)
      : [...currentValues, value];

    setFormData((prev) => ({ ...prev, [category]: newValues }));
  };

  const handleSubmit = () => {
    // Validate required fields
    if (!formData.clientIndustries) {
      setShowValidationError(true);
      return;
    }

    // Validate industries list if multiple industries is selected
    if (formData.clientIndustries === "multiple_industries" && formData.clientIndustriesList.length === 0) {
      setShowValidationError(true);
      return;
    }

    setShowValidationError(false);
    submit(formData);
  };

  return (
    <div className="max-h-[380px] overflow-y-auto scrollbar-none">
      <div className="mb-8">
        <Controller
          name="clientIndustries"
          control={control}
          rules={{ required: "This field is required" }}
          render={({ field: { onChange, value } }) => (
            <RadioInput
              register={register}
              name="clientIndustries"
              label="Do you recruit across industries or focus on specific ones?"
              validatorMessage={showValidationError && !formData.clientIndustries ? "This field is required" : ""}
              options={industryFocusOptions}
              type="radio"
              value={formData.clientIndustries}
              onChange={(e) => handleMultiSelect("clientIndustries", e.target.value)}
            />
          )}
        />
      </div>

      {formData.clientIndustries === "multiple_industries" && (
        <div className="mb-8">
          <label className="text-sm bg-transparent p-0 text-gray-600 mb-4 font-medium block">
            Select Client Industries <span className="text-red-500">*</span>
          </label>
          <div className="flex flex-wrap gap-3">
            {clientIndustryOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleMultiSelect("clientIndustriesList", option.value)}
                className={`px-4 py-[6px] !ml-0 rounded-full border text-sm font-medium hover:bg-white transition-colors ${formData?.clientIndustriesList?.includes(option.value)
                  ? "bg-[#EFF8FF] text-[#175CD3] border-[#84CAFF]"
                  : "bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200"
                  }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          {showValidationError && formData.clientIndustriesList.length === 0 && (
            <p className="text-xs text-red-500 mt-2">Please select at least one industry</p>
          )}
        </div>
      )}

      <div className="mb-8">
        <label className="text-sm bg-transparent p-0 text-gray-600 mb-4 font-medium block">
          Which values do your client companies most often emphasize?
        </label>
        <div className="flex flex-wrap gap-3">
          {clientValueOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleMultiSelect('clientValues', option.value)}
              className={`px-4 py-[6px] !ml-0 rounded-full border text-sm hover:bg-white font-medium transition-colors ${formData?.clientValues?.includes(option.value)
                ? 'bg-[#EFF8FF] text-[#175CD3] border-[#84CAFF]'
                : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-8">
        <label className="text-sm bg-transparent p-0 text-gray-600 mb-4 font-medium block">
          Agency-focused
        </label>
        <div className="flex flex-wrap gap-3">
          {agencyFocusOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleMultiSelect('agencyFocus', option.value)}
              className={`px-4 py-[6px] !ml-0 rounded-full border text-sm hover:bg-white font-medium transition-colors ${formData?.agencyFocus?.includes(option.value)
                ? 'bg-[#EFF8FF] text-[#175CD3] border-[#84CAFF]'
                : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-4 mt-8">
        <Button
          className="max-w-sm flex-1 border !border-[#012169] !text-[#012169] bg-white"
          type="outline"
          onClick={goBack}
        >
          Back
        </Button>
        <Button
          className="max-w-sm flex-1 !bg-[#012169] !border-none text-white"
          type="fill"
          onClick={handleSubmit}
        >
          Next
        </Button>
      </div>
    </div>
  );
};